/**
 * ApprovalDecisionBrief — decision view for a pending AI-run approval.
 *
 * Consolidates one approval request for the operator: the requested action, its exact
 * target, the reason, the risk, the expected result and the approve/reject path. Read-only:
 * the actual approve / edit-and-approve / reject controls remain AgentRunDetailPage's own
 * ApprovalControls; this panel explains what the operator is deciding on.
 */
import { useQuery } from "@tanstack/react-query";

import { useAuth } from "../context/AuthContext";
import { useAgentPermissions } from "../context/AgentPermissionsContext";
import { agentRunsApi } from "../services/api";
import { ticketStatusLabel } from "./ticketStatusVocabulary";

const REVIEWABLE = new Set(["awaiting_technician_review", "awaiting_approval"]);

const ACTION_LABELS = {
  add_comment:           "הוספת הערה פנימית",
  close_ticket:          "סגירת קריאה",
  escalate:              "הסלמה — העלאת עדיפות לגבוהה",
  send_customer_message: "שליחת הודעה ללקוח",
};

const RISK_META = {
  green: { label: "נמוך",   cls: "bg-green-50 text-green-700 border-green-200" },
  amber: { label: "בינוני", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  red:   { label: "גבוה",   cls: "bg-red-50 text-red-700 border-red-200" },
};

const INITIATED_LABELS = {
  "auto_trigger":         "אוטומטי",
  "auto:new_ticket":      "אוטומטי — קריאה חדשה",
  "auto:email_reply":     "אוטומטי — תגובת לקוח",
  "auto:scheduled_check": "אוטומטי — בדיקה מתוזמנת",
};

const TERMINAL_TICKET_STATUSES = new Set(["closed", "merged"]);

const CUSTOMER_MAIL_LABEL = {
  send_customer_message: "ההודעה שתישלח ללקוח",
  close_ticket:          "הודעת הסגירה שתישלח ללקוח",
  escalate:              "מסר האישור שיישלח ללקוח",
};

function Section({ title, testid, children }) {
  return (
    <section data-testid={testid} className="space-y-1.5">
      <h3 className="text-xs font-semibold text-gray-500 border-b pb-1">{title}</h3>
      {children}
    </section>
  );
}

function Row({ label, value }) {
  if (value == null || value === "") return null;
  return (
    <>
      <dt className="text-gray-500">{label}</dt>
      <dd className="text-gray-800 truncate" title={typeof value === "string" ? value : undefined}>{value}</dd>
    </>
  );
}

export default function ApprovalDecisionBrief({ run }) {
  const auth = useAuth();
  const { can } = useAgentPermissions();
  const reviewable = Boolean(run) && REVIEWABLE.has(run.status);
  const analysis = run?.analysis || null;

  // The expected effects are the *backend's* account of this decision, not this card's. It is the same
  // projection `ApprovalCenterPage` reads (`GET /api/agent-runs/{id}/approval-context`), and reading it
  // here is what keeps one decision from stating two different effect lists on the two surfaces that
  // show it. Before this, the card derived its own list from the proposed action type and hedged
  // undoability on `emails_customer`, so a `close_ticket` with no closing note was drawn as
  // "השפעה פנימית בלבד" — an internal, reversible-looking effect — while `get_run_approval_context`
  // was appending `_NO_COMPENSATION_APPROVAL_NOTE` for exactly that action, since the compensation
  // contract declares no undo for it. The approver read the reassuring sentence on this surface and the
  // irreversibility on the queue's, for the same pending action. Fetched only when the run carries an
  // analysis: with none, the card already says no expected result can be shown.
  const effectsQuery = useQuery({
    queryKey: ["approvalContext", run?.id],
    queryFn: () => agentRunsApi.approvalContext(run.id),
    enabled: reviewable && analysis != null,
    staleTime: 30000,
  });

  if (!reviewable) return null;

  const effects = effectsQuery.data ?? null;
  const effectsPostconditions = effects?.action?.postconditions ?? [];
  const effectsBlocking = effects?.action?.blocking_conditions ?? [];
  const effectsEmpty = effects != null && effectsPostconditions.length === 0 && effectsBlocking.length === 0;

  const a = analysis;
  const pa = a?.proposed_action || null;
  const actionType = pa?.action_type || null;
  const draft = (a?.customer_reply_draft || "").trim();
  const degraded = a?.degraded || null;

  const mayApprove = can("agents_approve");
  const currentEmail = (auth?.user?.email || "").toLowerCase();
  const initiatedBy = (run.initiated_by || "").toLowerCase();
  const selfApproval = Boolean(currentEmail) && currentEmail === initiatedBy;

  const connectionRequired = a ? a.connection_required !== false : null;
  const diagnosticDone = a ? a.diagnostic_completed === true : null;
  const needsConnectionFirst = connectionRequired === true && !diagnosticDone;
  const ticketTerminal = TERMINAL_TICKET_STATUSES.has(run.ticket_status);
  const ticketTerminalLabel = run.ticket_status ? ticketStatusLabel(run.ticket_status) : null;

  const risk = a?.risk_level ? RISK_META[a.risk_level] : null;
  const emailsCustomer = actionType === "send_customer_message"
    || ((actionType === "close_ticket" || actionType === "escalate") && Boolean(draft));

  const initiatedLabel = run.initiated_by
    ? (INITIATED_LABELS[run.initiated_by] ?? run.initiated_by.split("@")[0])
    : null;

  return (
    <section
      dir="rtl"
      data-testid="approval-decision-brief"
      aria-labelledby="approval-decision-brief-title"
      className="bg-white rounded-xl border-2 border-amber-300 shadow-sm p-5 space-y-4"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap border-b pb-3">
        <div>
          <h2 id="approval-decision-brief-title" className="text-sm font-bold text-gray-800">
            <span aria-hidden="true">⏳ </span>בקשת אישור — נדרשת החלטה אנושית
          </h2>
          <p className="text-xs text-gray-400 mt-0.5">
            סקירה מרוכזת של מה שממתין לאישור, לפני החלטה.
          </p>
        </div>
        <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-0.5 font-medium">
          {run.status === "awaiting_approval" ? "ממתין לאישור" : "ממתין לסקירה"}
        </span>
      </div>

      {degraded && (
        <div role="alert" data-testid="adb-degraded" className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
          {degraded === "mock"
            ? "זהו ניתוח דמה (ספק AI לא מוגדר) — הבקשה אינה מבוססת אבחון אמיתי."
            : "תשובת המודל לא נקראה כראוי — הבקשה אינה מבוססת אבחון אמין. מומלץ להריץ ניתוח מחדש לפני אישור."}
        </div>
      )}

      <Section title="הפעולה המבוקשת" testid="adb-action">
        {pa ? (
          <>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-semibold text-gray-800">{ACTION_LABELS[actionType] ?? actionType}</span>
              {emailsCustomer && (
                <span className="text-[11px] text-red-700 bg-red-50 border border-red-200 rounded-full px-2 py-0.5 font-medium">
                  כולל שליחת מייל ללקוח
                </span>
              )}
            </div>
            {pa.description && <p className="text-sm text-gray-700 leading-relaxed">{pa.description}</p>}
            {emailsCustomer && draft && (
              <div className="mt-1">
                <p className="text-xs font-medium text-gray-500 mb-1">{CUSTOMER_MAIL_LABEL[actionType]}:</p>
                <div dir="auto" className="bg-gray-50 rounded-lg border p-3 text-sm text-gray-700 whitespace-pre-wrap">
                  {draft}
                </div>
              </div>
            )}
          </>
        ) : (
          <p className="text-sm text-gray-500">
            {degraded
              ? "לא מוצגת פעולה מוצעת — הניתוח אינו אמין (דמה/כשל פירסור)."
              : "לא הוצעה פעולה. אישור יסיים את הריצה ללא ביצוע שינוי."}
          </p>
        )}
      </Section>

      <Section title="היעד המדויק" testid="adb-target">
        {pa?.target ? (
          <p className="text-sm text-gray-800">
            <span className="text-xs text-gray-500">יעד הפעולה: </span>
            <span dir="ltr" className="font-mono">{pa.target}</span>
          </p>
        ) : (
          <p className="text-sm text-gray-500">לא צוין יעד מדויק לפעולה.</p>
        )}
        {actionType === "send_customer_message" && (
          <p className="text-[11px] text-gray-400">
            ללא יעד, ההודעה תישלח לכתובת השולח של הקריאה. כתובת שאינה משויכת לקריאה תיחסם על ידי המערכת.
          </p>
        )}
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs pt-1">
          <Row label="קריאה" value={run.ticket_number ? `#${run.ticket_number}` : null} />
          <Row label="לקוח" value={run.ticket_company_name} />
          <Row label="טכנאי מטפל" value={run.ticket_technician_name ?? "טרם שויכה לטכנאי"} />
          <Row label="סטטוס הקריאה" value={ticketTerminalLabel} />
          <Row label="עובד דיגיטלי" value={run.agent_name} />
          <Row label="מקור ההפעלה" value={initiatedLabel} />
        </dl>
      </Section>

      <Section title="הסיבה" testid="adb-reason">
        {a ? (
          <>
            {a.likely_issue && (
              <p className="text-sm text-gray-800">
                <span className="text-xs text-gray-500">בעיה סבירה: </span>
                {a.likely_issue}
              </p>
            )}
            {typeof a.confidence === "number" && (
              <p className="text-xs text-gray-600">
                ביטחון הניתוח: <span className="font-semibold">{a.confidence}%</span>
                {a.confidence < 60 && (
                  <span className="mr-2 text-amber-600 font-medium">ביטחון נמוך — יש לשקול דחייה או מידע נוסף</span>
                )}
              </p>
            )}
            {a.missing_information?.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5">
                <p className="text-xs font-medium text-amber-700 mb-1">מידע חסר לבחינת הבקשה:</p>
                <ul className="text-xs text-amber-800 space-y-0.5 list-disc list-inside">
                  {a.missing_information.map((item, i) => <li key={i}>{item}</li>)}
                </ul>
              </div>
            )}
            {a.reasoning && (
              <details className="text-xs text-gray-500">
                <summary className="cursor-pointer hover:text-gray-700 select-none font-medium">נימוק המערכת</summary>
                <p dir="auto" className="mt-1 text-gray-600 whitespace-pre-wrap">{a.reasoning}</p>
              </details>
            )}
          </>
        ) : (
          <p className="text-sm text-gray-500">אין ניתוח זמין עדיין — לא ניתן להציג את הסיבה לבקשה.</p>
        )}
      </Section>

      <Section title="סיכון והשפעה" testid="adb-risk">
        <div className="flex items-center gap-2 flex-wrap">
          {risk ? (
            <span className={`text-xs font-medium border rounded-full px-2 py-0.5 ${risk.cls}`}>סיכון: {risk.label}</span>
          ) : (
            <span className="text-xs font-medium border rounded-full px-2 py-0.5 bg-gray-50 text-gray-500 border-gray-200">
              סיכון: לא ידוע
            </span>
          )}
          {/* Only external impact is asserted here, and only about the mail: a sent mail cannot be
              recalled. This card states no verdict of its own on whether the action can be undone —
              that answer belongs to the compensation contract and is rendered verbatim from the
              projection in "התוצאה הצפויה מהאישור". The arm that used to sit here
              ("השפעה פנימית בלבד — ללא מייל ללקוח") was read as its counterpart, i.e. as "reversible",
              and was drawn for exactly the actions the backend calls uncompensatable. */}
          {emailsCustomer && (
            <span className="text-xs text-red-700">השפעה חיצונית — מייל ללקוח אינו ניתן לביטול לאחר שליחה</span>
          )}
        </div>
        <ul className="text-xs text-gray-600 space-y-0.5">
          {run.ticket_priority === "high" && <li>⚠ הקריאה בעדיפות גבוהה</li>}
          {run.ticket_is_sla_breach && <li>⏰ הקריאה בהפרת SLA (פתוחה ללא טכנאי מעל 6 שעות)</li>}
          {ticketTerminal && (
            <li className="text-red-700">
              הקריאה {run.ticket_status === "closed" ? "כבר סגורה" : "מוזגה לקריאה אחרת"} — לפי המצב הנוכחי הפעולה לא תבוצע, והמערכת תדווח על כך ללא שינוי.
            </li>
          )}
        </ul>
      </Section>

      <Section title="התוצאה הצפויה מהאישור" testid="adb-expected">
        {!a ? (
          <p className="text-sm text-gray-500">אין ניתוח זמין — לא ניתן להציג תוצאה צפויה.</p>
        ) : effectsQuery.isPending ? (
          <p role="status" className="text-sm text-gray-400">טוען מהשרת מה תיעשה הפעולה…</p>
        ) : !effects ? (
          // An unreadable projection is a fact about the record, not an absence of one. The sentence
          // names the inference the reader must not make, because the fact that is missing here is the
          // one that says whether the action can be undone at all.
          <p
            data-testid="adb-effects-unreadable"
            className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1"
          >
            לא ניתן לקרוא מהשרת מה תיעשה הפעולה — אין להסיק מכך שהפעולה ניתנת לביטול. רעננו את העמוד לפני החלטה.
          </p>
        ) : (
          <>
            {!effects.action?.executes_on_approval && (
              <p className="text-sm text-gray-700">הפעולה לא תבוצע באישור זה — הריצה תסתיים בדיווח על אי-ביצוע.</p>
            )}
            <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
              {effectsPostconditions.map((item, i) => (
                <li key={`post-${i}`} data-testid="adb-effect-item">{item}</li>
              ))}
              {effectsBlocking.map((item, i) => (
                <li key={`block-${i}`} data-testid="adb-blocking-item" className="text-red-700">{item}</li>
              ))}
            </ul>
            {effectsEmpty && (
              <p className="text-sm text-gray-400">השרת לא דיווח על השפעה צפויה לפעולה זו.</p>
            )}
          </>
        )}
        {a?.recommended_next_step && !needsConnectionFirst && (
          <p className="text-xs text-gray-500">
            <span className="font-medium">המלצת המערכת להמשך: </span>
            {a.recommended_next_step}
          </p>
        )}
      </Section>

      <Section title="מסלול ההחלטה" testid="adb-path">
        <ul className="text-xs text-gray-700 space-y-1">
          <li>
            <span className="font-semibold text-green-700">אשר</span> — מאשר את הבקשה; ההשפעה בפועל מפורטת ב״תוצאה הצפויה״.
          </li>
          <li>
            <span className="font-semibold text-amber-600">ערוך ואשר</span> — תיקון תוכן הפעולה או התגובה נשמר ללימוד העובד הדיגיטלי ואז מאושר.
          </li>
          <li>
            <span className="font-semibold text-red-600">דחה</span> — הפעולה לא תבוצע; ניתן לצרף סיבה שתחזור לעובד הדיגיטלי.
          </li>
        </ul>
        {!mayApprove && (
          <p className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded px-2 py-1">
            אין לך הרשאת אישור (agents_approve) — התצוגה לקריאה בלבד והפקדים מוסתרים.
          </p>
        )}
        {mayApprove && selfApproval && (
          <p role="status" className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1">
            אישור עצמי חסום: הפעלת את הריצה, ולכן אישורה יסורב על ידי המערכת. יש להעביר למאשר אחר; דחייה מותרת.
          </p>
        )}
        <p className="text-[11px] text-gray-400">
          אישור משייך את הקריאה למאשר אם אינה משויכת לטכנאי. אישור קבוצתי מהתור אינו כולל פעולות עם מייל ללקוח.
        </p>
      </Section>
    </section>
  );
}
